import { defineComponent, h } from 'vue';
import {
    QCard,
    QCardSection,
    QCardActions,
    QBtn,
    QDialog,
    QTooltip,
    QIcon,
} from 'quasar';
import utils from '../../../Shared/utils.jsx';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import ControlPicker from './controlPicker.jsx';

const WIZARD_STEP_SCHEMA = '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_WizardStep';

export default defineComponent({
    name: 'cc-wizard-dsgn',
    mixins: [BaseComponentMixin],
    components: {
        QCard,
        QCardSection,
        QCardActions,
        QBtn,
        QDialog,
        QTooltip,
        ControlPicker,
        QIcon,
    },
    data() {
        return {
            current_step: 0,
            titleEval: null,
        };
    },
    mounted() {
        if (this.controlData?.Title) {
            try {
                this.titleEval = utils.compile(this, this.controlData.Title);
            }
            catch (e) {
                utils.warn('Wizard designer Title compile failed: ' + e);
            }
        }

        this.$watch(
            () => this.Root?.SelectedModel,
            (m) => {
                if (this.Root?.SelectedNode !== this) return;
                if (m && m.$typeSchema === WIZARD_STEP_SCHEMA && Array.isArray(this.controlData?.Steps)) {
                    const i = this.controlData.Steps.findIndex((s) => s === m);
                    if (i >= 0) this.current_step = i;
                }
            }
        );
    },
    computed: {
        selected$() {
            return this.Root?.SelectedNode && this.Root.SelectedModel === this.designmodel;
        },
        selectedStep$() {
            const steps = this.controlData?.Steps;
            if (!steps || this.current_step < 0 || this.current_step >= steps.length)
                return false;
            return this.Root?.SelectedNode && this.Root.SelectedModel === steps[this.current_step];
        },
        titleDisplay() {
            try {
                if (this.titleEval)
                    return utils.evaluate(this.titleEval, this);
                return this.controlData?.Title || '';
            }
            catch (e) {
                return this.controlData?.Title || '';
            }
        },
    },
    methods: {
        nextStep(e) {
            const steps = this.controlData?.Steps;
            if (!steps?.length) return;
            this.current_step++;
            if (this.current_step >= steps.length)
                this.current_step = 0;
            e.cancelBubble = true;
            e.stopPropagation();
        },
        previousStep(e) {
            const steps = this.controlData?.Steps;
            if (!steps?.length) return;
            this.current_step--;
            if (this.current_step < 0)
                this.current_step = steps.length - 1;
            e.cancelBubble = true;
            e.stopPropagation();
        },
        editStep(e) {
            const steps = this.controlData?.Steps;
            if (!steps?.length) return;
            const step = steps[this.current_step];
            this.Root.SelectNode(this, step);
            e.cancelBubble = true;
            e.stopPropagation();
        },
        async addStep(e) {
            e.cancelBubble = true;
            e.stopPropagation();

            const schema_ = await utils.schema.get(WIZARD_STEP_SCHEMA);
            const schema = utils.schema.resolve_Of(schema_);
            const model = utils.schema.createDefaultModel(schema, this.Root);

            if (!model.Title) model.Title = 'New Step';
            if (!model.Name) model.Name = 'Unnamed';

            if (!this.controlData.Steps)
                this.controlData.Steps = [];

            this.controlData.Steps.push(model);
            this.current_step = this.controlData.Steps.length - 1;
        },
        deleteStep(e) {
            const steps = this.controlData?.Steps;
            if (!steps?.length) return;
            if (this.current_step >= 0 && this.current_step < steps.length) {
                steps.splice(this.current_step, 1);
                if (this.current_step >= steps.length)
                    this.current_step = Math.max(0, steps.length - 1);
            }
            e.cancelBubble = true;
            e.stopPropagation();
        },
        async confirmAddControl(id) {
            this.pickervisible = false;

            try {
                const schema_ = await utils.schema.get(id);
                const schema = utils.schema.resolve_Of(schema_);
                const model = utils.schema.createDefaultModel(schema, this.Root);
                const steps = this.controlData?.Steps;

                if (this.pickertarget === 'footer' && steps?.length) {
                    const step = steps[this.current_step];
                    if (step) {
                        if (!step.FooterControls)
                            step.FooterControls = [];
                        step.FooterControls.push(model);
                        return;
                    }
                }

                if (this.pickertarget === 'body' && steps?.length) {
                    const step = steps[this.current_step];
                    if (step) {
                        if (!step.Controls)
                            step.Controls = [];
                        step.Controls.push(model);
                        return;
                    }
                }

                if (typeof this.pickertarget === 'string') {
                    switch (this.pickertarget) {
                        case 'header':
                            if (!this.controlData.HeaderControls)
                                this.controlData.HeaderControls = [];
                            this.controlData.HeaderControls.push(model);
                            return;
                        case 'body':
                            if (!this.controlData.Controls)
                                this.controlData.Controls = [];
                            this.controlData.Controls.push(model);
                            return;
                    }
                }
                else if (typeof this.pickertarget === 'object' && Array.isArray(this.pickertarget)) {
                    this.pickertarget.push(model);
                }
            }
            catch (e) {
                alert(e);
            }
        },
    },
    props: {
    },
    render() {
        const steps = this.controlData?.Steps;
        const hasSteps = Array.isArray(steps) && steps.length > 0;

        let stepPanel = null;
        if (hasSteps && this.current_step >= 0 && this.current_step < steps.length) {
            const step = steps[this.current_step];

            const body = [];
            if (step.Controls) {
                for (let i = 0; i < step.Controls.length; i++) {
                    const control = step.Controls[i];
                    if (!control.$objectId) control.$objectId = utils.generateUUID();

                    const DynamicControl = utils.getDesignerComponent(control, null);
                    const _k = `${i}_${control.ControlType}_${control.$objectId}`;

                    body.push(
                        <DynamicControl
                            key={_k}
                            type={control.ControlType}
                            name={control.ControlData?.Name}
                            root={this.root}
                            designmodel={control}
                            parentType="VerticalLayout"
                            controlData={control.ControlData}
                            controlURL={control.ControlURL}
                            sourceIndex={i}
                        />
                    );
                }
            }

            if (this.selectedStep$) {
                body.push(
                    <QBtn
                        key="add-body"
                        flat
                        size="sm"
                        icon="mdi-plus-circle"
                        color="blue-8"
                        style={{ zIndex: 992 }}
                        onClick={(e) => this.addControl(e, 'body')}>
                        <QTooltip>Add control to step body</QTooltip>
                    </QBtn>
                );
            }

            const footer = [];
            if (step.FooterControls) {
                for (let i = 0; i < step.FooterControls.length; i++) {
                    const control = step.FooterControls[i];
                    if (!control.$objectId) control.$objectId = utils.generateUUID();

                    const DynamicControl = utils.getDesignerComponent(control, null);
                    const _k = `f_${i}_${control.ControlType}_${control.$objectId}`;

                    footer.push(
                        <div key={_k} style={{ float: 'left' }}>
                            <DynamicControl
                                type={control.ControlType}
                                name={control.ControlData?.Name}
                                root={this.root}
                                designmodel={control}
                                parentType="HorizontalLayout"
                                controlData={control.ControlData}
                                controlURL={control.ControlURL}
                                sourceIndex={i}
                            />
                        </div>
                    );
                }
            }

            if (this.selectedStep$) {
                footer.push(
                    <QBtn
                        key="add-footer"
                        flat
                        size="sm"
                        icon="mdi-plus-circle"
                        color="green-8"
                        style={{ zIndex: 992 }}
                        onClick={(e) => this.addControl(e, 'footer')}>
                        <QTooltip>Add control to step footer</QTooltip>
                    </QBtn>
                );
            }

            stepPanel = (
                <div key="step-wrap" class="column fit" style="align-items: flex-start;">
                    <QCardSection class="q-pb-none">{body}</QCardSection>
                    <QCardActions class="q-mt-auto">{footer}</QCardActions>
                </div>
            );
        }

        const cardStyle = {
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            width: '100%',
            position: 'relative',
            ...utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent),
        };

        const toolLink = {
            opacity: 0.5,
            color: 'blueviolet',
            cursor: 'pointer',
            fontSize: 'large',
            lineHeight: 1,
            userSelect: 'none',
        };

        /** Single overlay row — do not use per-item position:absolute (breaks in flex layouts). */
        const designToolbar = this.selected$ ? (
            <div
                key="wizard-dsgn-toolbar"
                class="wizard-dsgn-toolbar"
                style={{
                    position: 'absolute',
                    top: '6px',
                    right: '8px',
                    zIndex: 40,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '6px',
                    pointerEvents: 'auto',
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    e.cancelBubble = true;
                }}>
                <span title="Add Step" style={toolLink} onClick={(e) => this.addStep(e)}>
                    <i class="mdi mdi-plus-circle" />
                </span>
                <span title="View Previous Step" style={toolLink} onClick={(e) => this.previousStep(e)}>
                    <i class="mdi mdi-menu-left" />
                </span>
                <span
                    title="Edit Step"
                    style={{ ...toolLink, fontSize: 'medium' }}
                    onClick={(e) => this.editStep(e)}>
                    [ {this.current_step + 1} ]
                </span>
                <span title="View Next Step" style={toolLink} onClick={(e) => this.nextStep(e)}>
                    <i class="mdi mdi-menu-right" />
                </span>
                <span title="Delete Step" style={toolLink} onClick={(e) => this.deleteStep(e)}>
                    <i class="mdi mdi-close-circle-outline" />
                </span>
            </div>
        ) : null;

        const showCrumbs =
            this.controlData.ShowBreadCrumb
            || this.controlData.ShowBreadcrumb;

        let breadcrumbs;
        if (showCrumbs && hasSteps) {
            const crumbs = [];
            for (let i = 0; i < steps.length && i < this.current_step + 1; i++) {
                crumbs.push(
                    <span
                        key={`bc-${i}`}
                        style={{
                            margin: '2px',
                            paddingLeft: '4px',
                            paddingRight: '4px',
                            float: 'left',
                            backgroundColor: '#e8e8e8',
                            borderRadius: '8px',
                        }}>
                        {steps[i].Title}
                    </span>
                );
                if (i < this.current_step)
                    crumbs.push(
                        <span key={`bcsep-${i}`} style={{ marginTop: '2px', float: 'left' }}>
                            <QIcon name="mdi-chevron-right" size="16px" />
                        </span>
                    );
            }
            breadcrumbs = <div style={{ fontSize: 'smaller', padding: '5px' }}>{crumbs}</div>;
        }

        return (
            <QCard
                class={{
                    'c-Wizard': true,
                    column: true,
                    [`c-name-${this.name || 'unnamed'}`]: true,
                    'designer-container': true,
                    selected: this.selected$,
                    'q-ma-xs': true,
                }}
                style={cardStyle}
                flat
                onClick={(e) => this.handleClick(e)}>
                <div
                    class={{ 'designer-container-hovering': true, 'designer-container-selected': this.selected$ }}
                />

                {designToolbar}

                <QCardSection class="text-h4 text-white q-py-sm" style={{ backgroundColor: 'var(--v-navigation-base)' }}>{this.titleDisplay}</QCardSection>

                {breadcrumbs}

                <div
                    class={{ 'designer-container': true, selected: this.selectedStep$ }}
                    style={{ padding: '6px', position: 'relative' }}
                    onClick={(e) => this.editStep(e)}>
                    <div
                        class={{
                            'designer-container-hovering': true,
                            'designer-container-selected': this.selectedStep$,
                        }}
                    />
                    {stepPanel}
                </div>

                {this.generatePicker(h)}
            </QCard>
        );
    },
});
